import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  doc,
  onSnapshot,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  updateDoc,
  setDoc,
  deleteDoc,
  addDoc,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import {
  GoogleAuthProvider,
  reauthenticateWithPopup,
} from "firebase/auth";
import { db, storage, auth } from "../firebaseConfig";
import { FaUserAlt, FaCloudUploadAlt } from "react-icons/fa";
import moment from "moment";
import AdCard from "../components/AdCard";

const googleProvider = new GoogleAuthProvider();

/**
 * A helper function to format "Member since ...".
 */
const monthAndYear = (firestoreTimestamp) => {
  if (!firestoreTimestamp) return "???";
  const date = firestoreTimestamp.toDate();
  return `${moment(date).format("MMM")} ${moment(date).format("YYYY")}`;
};

/**
 * A helper function to resize images before uploading.
 */
async function resizeImageFile(file, maxSize = 500) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height *= maxSize / width));
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = Math.round((width *= maxSize / height));
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              return reject(new Error("Canvas is empty"));
            }
            const newFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(newFile);
          },
          file.type,
          0.9
        );
      };
      img.onerror = (err) => reject(err);
      img.src = e.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

/**
 * Удаляем все объявления (ads) пользователя.
 */
async function removeUserAds(uid) {
  const adsRef = collection(db, "ads");
  const qAds = query(adsRef, where("postedBy", "==", uid));
  const snap = await getDocs(qAds);

  for (const docSnap of snap.docs) {
    const adData = docSnap.data();
    // Удалить фотографии
    if (adData.images && Array.isArray(adData.images)) {
      for (const img of adData.images) {
        if (img.path) {
          await deleteObject(ref(storage, img.path)).catch(() =>
            console.log("Could not remove image from storage:", img.path)
          );
        }
      }
    }
    // Удалить документ объявления
    await deleteDoc(doc(db, "ads", docSnap.id));
  }
}

/**
 * Удаляем все чаты (messages) пользователя:
 */
async function removeUserChats(uid) {
  const msgRef = collection(db, "messages");
  const qMsg = query(msgRef, where("users", "array-contains", uid));
  const msgsSnap = await getDocs(qMsg);

  for (const msgDoc of msgsSnap.docs) {
    const chatId = msgDoc.id;

    // Удаляем подколлекцию "chat" (все сообщения)
    const chatRef = collection(db, "messages", chatId, "chat");
    const chatSnap = await getDocs(chatRef);
    for (const c of chatSnap.docs) {
      await deleteDoc(doc(db, "messages", chatId, "chat", c.id));
    }

    // Удаляем сам документ "messages/{chatId}"
    await deleteDoc(doc(db, "messages", chatId));
  }
}

const Profile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [img, setImg] = useState("");

  const [ads, setAds] = useState([]);
  const [ratingList, setRatingList] = useState([]);
  const [ratingValue, setRatingValue] = useState("5");
  const [avgRating, setAvgRating] = useState(null);
  const [ratingCount, setRatingCount] = useState(0);

  const [reviewList, setReviewList] = useState([]);
  const [reviewValue, setReviewValue] = useState("");
  const [hasReview, setHasReview] = useState(false);

  // Check if this profile belongs to the logged-in user
  const isOwner = auth.currentUser && auth.currentUser.uid === id;

  /**
   * Effect A: subscribe to user doc, fetch ads, ratings, reviews
   */
  useEffect(() => {
    if (!id) return;
    setUser(null);

    const unsub = onSnapshot(doc(db, "users", id), (snap) => {
      if (snap.exists()) {
        setUser(snap.data());
      } else {
        setUser(null);
      }
    });

    const getAds = async () => {
      const qAds = query(
        collection(db, "ads"),
        where("postedBy", "==", id),
        orderBy("publishedAt", "desc")
      );
      const docsSnap = await getDocs(qAds);
      let arr = [];
      docsSnap.forEach((d) => arr.push({ ...d.data() }));
      setAds(arr);
    };

    const getRatings = async () => {
      const rRef = collection(db, "users", id, "ratings");
      const snap = await getDocs(rRef);
      let temp = [];
      snap.forEach((doc) => {
        temp.push({ ...doc.data(), raterId: doc.id });
      });
      setRatingList(temp);

      if (temp.length > 0) {
        const sum = temp.reduce((acc, item) => acc + item.rating, 0);
        setAvgRating(sum / temp.length);
        setRatingCount(temp.length);
      } else {
        setAvgRating(null);
        setRatingCount(0);
      }
    };

    const getReviews = async () => {
      const reviewsRef = collection(db, "users", id, "reviews");
      const qReviews = query(reviewsRef, orderBy("createdAt", "desc"));
      const snap = await getDocs(qReviews);
      let arr = [];
      snap.forEach((doc) => {
        arr.push({ ...doc.data(), reviewId: doc.id });
      });
      setReviewList(arr);
    };

    getAds();
    getRatings();
    getReviews();

    return () => unsub();
  }, [id]);

  /**
   * Effect B: upload new photo if "img" changes
   */
  useEffect(() => {
    const uploadImage = async () => {
      if (!user || !auth.currentUser) return;
      if (auth.currentUser.uid !== id) return;
      if (!img) return;

      const resizedFile = await resizeImageFile(img, 500);
      const imgRef = ref(storage, `profile/${Date.now()} - ${resizedFile.name}`);

      if (user.photoUrl && user.photoPath) {
        await deleteObject(ref(storage, user.photoPath)).catch(() => {
          console.log("Could not remove old photo");
        });
      }

      const result = await uploadBytes(imgRef, resizedFile);
      const url = await getDownloadURL(result.ref);

      await updateDoc(doc(db, "users", auth.currentUser.uid), {
        photoUrl: url,
        photoPath: result.ref.fullPath,
      });

      setImg("");
    };

    if (img) {
      uploadImage();
    }
  }, [img, user, id]);

  /**
   * Effect C: check if user has already left a review
   */
  useEffect(() => {
    if (!id || !auth.currentUser) {
      setHasReview(false);
      return;
    }
    if (auth.currentUser.uid === id) {
      setHasReview(false);
      return;
    }

    const checkReview = async () => {
      const reviewDocRef = doc(db, "users", id, "reviews", auth.currentUser.uid);
      const docSnap = await getDoc(reviewDocRef);
      setHasReview(docSnap.exists());
    };

    checkReview();
  }, [id]);

  if (!user) {
    return <div>Loading user...</div>;
  }

  // Handle rating
  const handleRate = async (e) => {
    e.preventDefault();
    if (!auth.currentUser || isOwner) return;

    const ratingNum = Number(ratingValue);
    await setDoc(doc(db, "users", id, "ratings", auth.currentUser.uid), {
      rating: ratingNum,
      createdAt: new Date(),
    });

    const rRef = collection(db, "users", id, "ratings");
    const snap = await getDocs(rRef);
    let temp = [];
    snap.forEach((doc) => {
      temp.push({ ...doc.data(), raterId: doc.id });
    });
    setRatingList(temp);

    if (temp.length > 0) {
      const sum = temp.reduce((acc, item) => acc + item.rating, 0);
      setAvgRating(sum / temp.length);
      setRatingCount(temp.length);
    } else {
      setAvgRating(null);
      setRatingCount(0);
    }
  };

  // Handle review
  const handleReview = async (e) => {
    e.preventDefault();
    if (!auth.currentUser || isOwner) return;
    if (!reviewValue.trim()) return;

    const meRef = doc(db, "users", auth.currentUser.uid);
    const meSnap = await getDoc(meRef);
    let authorName = "Unknown User";
    if (meSnap.exists()) {
      const meData = meSnap.data();
      if (meData.name) {
        authorName = meData.name;
      }
    }

    await setDoc(doc(db, "users", id, "reviews", auth.currentUser.uid), {
      text: reviewValue.trim(),
      authorId: auth.currentUser.uid,
      authorName,
      createdAt: new Date(),
    });

    setReviewValue("");
    setHasReview(true);

    const reviewsRef = collection(db, "users", id, "reviews");
    const qReviews = query(reviewsRef, orderBy("createdAt", "desc"));
    const snap2 = await getDocs(qReviews);
    let arr = [];
    snap2.forEach((doc) => {
      arr.push({ ...doc.data(), reviewId: doc.id });
    });
    setReviewList(arr);
  };

  // Delete photo
  const deletePhoto = async () => {
    if (!isOwner) return;
    const confirmDelete = window.confirm("Delete photo permanently?");
    if (!confirmDelete) return;

    if (user.photoPath) {
      await deleteObject(ref(storage, user.photoPath)).catch(() =>
        console.log("Could not remove photo from storage")
      );
    }

    await updateDoc(doc(db, "users", auth.currentUser.uid), {
      photoUrl: "",
      photoPath: "",
    });
  };

  // Delete user profile (ads, chats) and re-auth if needed
  const handleDeleteProfile = async () => {
    if (!isOwner) return;

    const answer = window.confirm(
      "Are you sure you want to delete your account permanently? All data will be lost."
    );
    if (!answer) return;

    const uid = auth.currentUser.uid;

    try {
      // 1) Удаляем объявления
      await removeUserAds(uid);

      // 2) Удаляем чаты
      await removeUserChats(uid);

      // 3) Удаляем документ "users/{uid}"
      await deleteDoc(doc(db, "users", uid));

      // 4) Удаляем аккаунт из Auth
      await auth.currentUser.delete();

      // Готово, уводим на главную
      navigate("/");
    } catch (error) {
      if (error.code === "auth/requires-recent-login") {
        try {
          // реавторизуем через Google, если пользователь — Google
          await reauthenticateWithPopup(auth.currentUser, googleProvider);

          // После реавторизации повторяем удаление
          await removeUserAds(uid);
          await removeUserChats(uid);
          await deleteDoc(doc(db, "users", uid));
          await auth.currentUser.delete();

          navigate("/");
        } catch (innerErr) {
          console.error("Re-auth error:", innerErr);
          alert("Please re-login or try again later.");
        }
      } else {
        console.error("Error deleting profile:", error);
        alert("Failed to delete account.");
      }
    }
  };

  return (
    <div className="mt-5 container row">
      {/* Left column */}
      <div className="text-center col-sm-2 col-md-3">
        {user.photoUrl ? (
          <img
            src={user.photoUrl}
            alt={user.name || "User"}
            style={{
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        ) : (
          <FaUserAlt size={50} />
        )}

        {isOwner && (
          <>
            {/* Кнопка Edit со сплывающим меню для загрузки/удаления фото */}
            <div className="dropdown my-3 text-center">
              <button
                className="btn btn-secondary btn-sm dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Edit
              </button>
              <ul className="dropdown-menu">
                <li>
                  <label htmlFor="photo" className="dropdown-item">
                    <FaCloudUploadAlt size={30} /> Upload Photo
                  </label>
                  <input
                    type="file"
                    id="photo"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => setImg(e.target.files[0])}
                  />
                </li>
                {user.photoUrl ? (
                  <li className="dropdown-item btn" onClick={deletePhoto}>
                    Remove Photo
                  </li>
                ) : null}
              </ul>
            </div>

            {/* Кнопка удаления профиля - под кнопкой Edit */}
            <div className="text-center mt-3">
              <button
                className="btn btn-danger btn-sm"
                onClick={handleDeleteProfile}
              >
                Delete My Profile
              </button>
            </div>
          </>
        )}

        {user.createdAt ? (
          <p className="mt-3">Member since {monthAndYear(user.createdAt)}</p>
        ) : (
          <p className="mt-3">Member since unknown date</p>
        )}
      </div>

      {/* Right column */}
      <div className="col-sm-10 col-md-9">
        <h3>{user.name || "Unnamed User"}</h3>

        {/* Email только для владельца */}
        {isOwner && user.email && (
          <p>
            <strong>Email:</strong> {user.email}
          </p>
        )}

        {/* RATING */}
        {ratingCount > 0 ? (
          <p>
            Rating: {avgRating?.toFixed(1)} / 5{" "}
            <span style={{ fontSize: "0.9rem", color: "#777" }}>
              ({ratingCount} {ratingCount === 1 ? "vote" : "votes"})
            </span>
          </p>
        ) : (
          <p>No ratings yet</p>
        )}

        {/* Форма рейтинга если не владелец */}
        {auth.currentUser && !isOwner && (
          <form onSubmit={handleRate} style={{ maxWidth: "200px" }}>
            <label className="form-label">Rate this user:</label>
            <select
              className="form-select mb-2"
              value={ratingValue}
              onChange={(e) => setRatingValue(e.target.value)}
            >
              <option value="1">1 (Poor)</option>
              <option value="2">2</option>
              <option value="3">3 (Neutral)</option>
              <option value="4">4</option>
              <option value="5">5 (Excellent)</option>
            </select>
            <button type="submit" className="btn btn-primary btn-sm">
              Submit Rating
            </button>
          </form>
        )}

        <hr />

        {/* REVIEWS */}
        <h4>Reviews</h4>
        {reviewList.length > 0 ? (
          <div className="mb-4">
            {reviewList.map((r) => (
              <div
                key={r.reviewId}
                className="card p-2 mb-3"
                style={{ background: "#f8f9fa" }}
              >
                <p>{r.text}</p>
                <small className="text-muted">
                  by{" "}
                  <Link to={`/profile/${r.authorId}`}>
                    {r.authorName || "Unknown User"}
                  </Link>
                  ,{" "}
                  {r.createdAt?.toDate
                    ? moment(r.createdAt.toDate()).fromNow()
                    : "Invalid date"}
                </small>
              </div>
            ))}
          </div>
        ) : (
          <p>No reviews yet.</p>
        )}

        {/* REVIEW FORM if not owner & no existing review */}
        {auth.currentUser && !isOwner && !hasReview && (
          <form onSubmit={handleReview} style={{ maxWidth: "400px" }}>
            <div className="mb-2">
              <label className="form-label">Write a review:</label>
              <textarea
                className="form-control"
                rows="3"
                value={reviewValue}
                onChange={(e) => setReviewValue(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-primary btn-sm">
              Submit Review
            </button>
          </form>
        )}

        <hr />

        {/* ADS */}
        {ads.length ? (
          <h4>Published Ads</h4>
        ) : (
          <h4>There are no ads published by this user</h4>
        )}
        <div className="row">
          {ads.map((ad) => (
            <div key={ad.adId} className="col-sm-6 col-md-4 mb-3">
              <AdCard ad={ad} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Profile;
